import { initializePaddle, Paddle, PaddleEventData } from '@paddle/paddle-js';

/**
 * Paddle.js, loaded once per page. `Paddle.Initialize` may only run once, and its
 * `eventCallback` is page-wide, so events fan out to whoever is listening right now
 * (`onPaddleEvent`) rather than being bound to one checkout.
 *
 * The client-side token is public by design (`test_…` sandbox / `live_…`); see
 * features/paddle-billing/notes/account-setup.md.
 */
type Listener = (event: PaddleEventData) => void;

const listeners = new Set<Listener>();
let instance: Promise<Paddle> | null = null;

export const getPaddle = (): Promise<Paddle> => {
  if (!instance) {
    const token = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
    if (!token) return Promise.reject(new Error('Payments are not configured.'));
    instance = initializePaddle({
      token,
      environment: process.env.REACT_APP_PADDLE_ENVIRONMENT === 'sandbox' ? 'sandbox' : 'production',
      eventCallback: (event) => listeners.forEach((listener) => listener(event)),
    }).then((paddle) => {
      if (!paddle) throw new Error('Could not load checkout.');
      return paddle;
    });
    // A failed load (blocked script, offline) shouldn't poison every later attempt.
    instance.catch(() => {
      instance = null;
    });
  }
  return instance;
};

export const onPaddleEvent = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Open the overlay for a transaction our backend created. */
export const openCheckout = async (transactionId: string): Promise<void> => {
  const paddle = await getPaddle();
  paddle.Checkout.open({
    transactionId,
    settings: {
      displayMode: 'overlay',
      theme: 'light',
      // The transaction is already tied to the signed-in account's Paddle customer.
      allowLogout: false,
    },
  });
};

export const closeCheckout = async (): Promise<void> => {
  (await getPaddle()).Checkout.close();
};
